// PageHeader — shell-owned header bar primitive (CHG-1225).
//
// Reused across ListPage, DetailPage, future Settings shell, DashboardPage,
// wizard chrome, and any custom standalone page that needs a consistent
// chrome-aligned header. Slim by design — no business logic, no data
// fetching, no router awareness beyond what the optional `backTo` prop
// implies.
//
// Per specs/_product/dashboard-v2-stage-treatments.md (chrome usage).
// Per feedback_composition_system.md (composition primitives are mandatory).
//
// CHG-1291 (minor-version bump, 1 of 4 in the audit-approved primitive
// extension sequence): PageHeaderAction gained the optional
// `permissionGate?: { require: string; fallback?: "hide" | "disable" }`
// field. When set, the action consults the CHG-1289 useHasPermission
// hook. The per-action GatedActionButton component below ONLY calls the
// hook when a gate is declared — so existing ungated callers (and their
// tests) continue to work without a QueryClientProvider context. Default
// fallback is "disable" (visible-but-inert with tooltip); "hide" omits.
// The CHG-1290 GATE_CATALOG is the canonical source of permission-code
// strings; future per-page CHGs wire CATALOG[entity_x_list].action_id
// into this prop.

import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { useHasPermission } from "../../auth/usePermissions";

export interface PageHeaderActionPermissionGate {
  /** Required permission code (e.g. "alora-giving.donor.create"). */
  require: string;
  /** Behavior when the user lacks the permission. Defaults to "disable". */
  fallback?: "hide" | "disable";
}

export interface PageHeaderAction {
  id: string;
  label: string;
  onClick?: () => void;
  href?: string;
  icon?: ReactNode;
  variant?: "primary" | "secondary" | "ghost" | "brand";
  disabled?: boolean;
  /** CHG-1291 — optional RBAC gate. Hooks only fire when this is set. */
  permissionGate?: PageHeaderActionPermissionGate;
}

export interface PageHeaderBreadcrumb {
  label: string;
  href?: string;
}

export interface PageHeaderProps {
  title: string;
  subtitle?: ReactNode;
  /** When set, shows a back-arrow link to this route (sm; left of title). */
  backTo?: string;
  breadcrumbs?: PageHeaderBreadcrumb[];
  /** Inline badges below the title (e.g., StageChip). */
  badges?: ReactNode;
  /** Right-aligned action buttons. */
  actions?: PageHeaderAction[];
  icon?: ReactNode;
}

const VARIANT_CLASS: Record<NonNullable<PageHeaderAction["variant"]>, string> = {
  primary: "btn btn-primary",
  secondary: "btn btn-secondary",
  ghost: "btn btn-ghost",
  brand: "btn btn-brand",
};

const DENIED_TOOLTIP = "You don't have permission to perform this action";

// ─────────────────────────────────────────────────────────────────
// Plain action button — no hook calls. Used when the action has no
// permissionGate. Keeps ungated callers + their tests working without
// a QueryClientProvider context.
// ─────────────────────────────────────────────────────────────────

function PlainActionButton({ action }: { action: PageHeaderAction }) {
  const cls = VARIANT_CLASS[action.variant ?? "secondary"];
  if (action.href) {
    return (
      <Link to={action.href} className={cls} aria-disabled={action.disabled || undefined}>
        {action.icon}
        {action.label}
      </Link>
    );
  }
  return (
    <button
      type="button"
      className={cls}
      onClick={action.onClick}
      disabled={action.disabled}
    >
      {action.icon}
      {action.label}
    </button>
  );
}

// ─────────────────────────────────────────────────────────────────
// Gated action button — calls useHasPermission. Only rendered when
// the action has a permissionGate declared.
// ─────────────────────────────────────────────────────────────────

function GatedActionButton({ action }: { action: PageHeaderAction }) {
  const gate = action.permissionGate!;
  const allowed = useHasPermission(gate.require);
  if (allowed) {
    return <PlainActionButton action={action} />;
  }
  const fallback = gate.fallback ?? "disable";
  if (fallback === "hide") {
    return null;
  }
  // fallback === "disable" — render visible-but-inert with tooltip.
  const cls = VARIANT_CLASS[action.variant ?? "secondary"];
  return (
    <button
      type="button"
      className={cls}
      title={DENIED_TOOLTIP}
      aria-disabled="true"
      disabled
      data-permission-gate-state="denied"
      data-permission-gate-require={gate.require}
    >
      {action.icon}
      {action.label}
    </button>
  );
}

export function PageHeader({
  title,
  subtitle,
  backTo,
  breadcrumbs,
  badges,
  actions,
  icon,
}: PageHeaderProps) {
  return (
    <header
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 8,
        padding: "20px 0 16px",
        borderBottom: "1px solid var(--color-border-subtle)",
        marginBottom: 20,
      }}
    >
      {breadcrumbs && breadcrumbs.length > 0 ? (
        <nav style={{ fontSize: 12, color: "var(--color-text-muted)" }} aria-label="Breadcrumb">
          {breadcrumbs.map((b, i) => (
            <span key={`${b.label}-${i}`}>
              {i > 0 ? <span style={{ margin: "0 6px" }}>/</span> : null}
              {b.href ? <Link to={b.href} style={{ color: "inherit" }}>{b.label}</Link> : b.label}
            </span>
          ))}
        </nav>
      ) : null}

      {/* flexWrap:"wrap" lets the actions group drop below the title when
          the row is narrower than its intrinsic width. Without it the
          actions group (flexShrink:0) squeezes the title block at 375px,
          producing the title-overlap-CTAs bug captured in
          /tmp/v2-field-test-r3-report.md §"Mobile 375px" + ROOT CAUSE #2. */}
      <div style={{ display: "flex", flexWrap: "wrap", alignItems: "flex-end", justifyContent: "space-between", gap: 16 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12, minWidth: 0, flex: "1 1 auto" }}>
          {backTo ? (
            <Link
              to={backTo}
              aria-label="Back"
              style={{
                fontSize: 14,
                color: "var(--color-text-muted)",
                textDecoration: "none",
                lineHeight: 1,
              }}
            >
              ←
            </Link>
          ) : null}
          {icon ? <span style={{ display: "inline-flex" }}>{icon}</span> : null}
          <div style={{ minWidth: 0 }}>
            <h1
              className="font-display"
              style={{
                fontSize: 24,
                fontWeight: 500,
                lineHeight: 1.15,
                margin: 0,
                color: "var(--color-text)",
              }}
            >
              {title}
            </h1>
            {subtitle ? (
              <div style={{ fontSize: 13, color: "var(--color-text-muted)", marginTop: 4 }}>{subtitle}</div>
            ) : null}
          </div>
        </div>

        {actions && actions.length > 0 ? (
          <div style={{ display: "flex", gap: 8, flexShrink: 0 }}>
            {actions.map((a) =>
              a.permissionGate
                ? <GatedActionButton key={a.id} action={a} />
                : <PlainActionButton key={a.id} action={a} />
            )}
          </div>
        ) : null}
      </div>

      {badges ? <div style={{ display: "flex", gap: 8, alignItems: "center" }}>{badges}</div> : null}
    </header>
  );
}
